import React, { createContext, useContext, useRef, useState } from "react";
import { trackFileDownload, trackFileDownloadFailed } from "../../lib/analytics";
import WindowsInstallModal from "../../component/Modals/WindowsInstallModal";
import MacInstallModal from "../../component/Modals/MacInstallModal";

const DownloadContext = createContext(null);

export const useDownload = () => useContext(DownloadContext);

const formatMB = (bytes) => (bytes / (1024 * 1024)).toFixed(1);

const detectPlatform = () => {
  if (typeof navigator === "undefined") return "windows";
  const ua = navigator.userAgent.toLowerCase();
  if (
    ua.includes("mac") &&
    !ua.includes("iphone") &&
    !ua.includes("ipad")
  ) {
    return "mac";
  }
  return "windows";
};

export const DownloadProvider = ({ children }) => {
  const [progress, setProgress] = useState(0);
  const [downloadedBytes, setDownloadedBytes] = useState(0);
  const [totalBytes, setTotalBytes] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState(null);
  const [installModalPlatform, setInstallModalPlatform] = useState(null);

  const openInstallModal = (platform) => {
    setInstallModalPlatform(platform || detectPlatform());
  };

  const closeInstallModal = () => setInstallModalPlatform(null);

  const DEFAULT_URL =
    process.env.REACT_APP_INSTALLER_URL ||
    "/ztaupdate/ZeroTypingAI.msi";

  // `isDownloading` state is stale inside a click handler's closure, so a
  // double-click could start two 125 MB fetches; the ref is always current.
  const inFlightRef = useRef(false);

  /**
   * Streams the installer with progress and saves it when complete. Every
   * Windows download CTA goes through here - a plain <a href> download is
   * invisible to JS, so progress and the `file_download` completion event
   * only exist on this path. `location` names the CTA for analytics.
   */
  const startDownload = async ({
    url = DEFAULT_URL,
    filename = "ZeroTypingAI.msi",
    platform = "windows",
    location = "unknown",
  } = {}) => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    openInstallModal(platform);
    setIsDownloading(true);
    setIsComplete(false);
    setError(null);
    setProgress(0);
    setDownloadedBytes(0);
    setTotalBytes(0);

    const startedAt = Date.now();
    const fileExtension = filename.includes(".") ? filename.split(".").pop() : null;

    // eslint-disable-next-line no-console
    console.info("[ZT download] starting", url);

    try {
      const response = await fetch(url, { mode: "cors", cache: "no-store" });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status} ${response.statusText}`);
      }
      if (!response.body) {
        throw new Error("Response has no body (browser cannot stream)");
      }

      const total = Number(response.headers.get("Content-Length") || 0);
      setTotalBytes(total);
      if (!total) {
        // eslint-disable-next-line no-console
        console.warn(
          "[ZT download] server did not send Content-Length - progress % will not be accurate"
        );
      }

      const reader = response.body.getReader();
      const chunks = [];
      let received = 0;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        received += value.length;
        setDownloadedBytes(received);
        if (total) {
          setProgress((received / total) * 100);
        }
      }

      const blob = new Blob(chunks);
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Revoking synchronously can cancel the save in Firefox/Safari; give the
      // browser time to open the blob first.
      setTimeout(() => URL.revokeObjectURL(blobUrl), 10_000);

      setProgress(100);
      setIsComplete(true);
      // The whole file is on disk at this point - the real "download complete".
      trackFileDownload({
        platform,
        file_name: filename,
        file_extension: fileExtension,
        link_url: url,
        location,
        total_bytes: total || received,
        duration_seconds: Math.round((Date.now() - startedAt) / 1000),
      });
      // eslint-disable-next-line no-console
      console.info("[ZT download] complete");
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(
        "[ZT download] stream failed - falling back to direct download. Reason:",
        err
      );
      setError(err);
      trackFileDownloadFailed({
        platform,
        file_name: filename,
        link_url: url,
        location,
        error_message: String(err?.message || err),
      });
      // Fallback: let the browser handle it directly (no progress tracking)
      const a = document.createElement("a");
      a.href = url;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } finally {
      inFlightRef.current = false;
      setIsDownloading(false);
    }
  };

  return (
    <DownloadContext.Provider
      value={{
        progress,
        downloadedBytes,
        totalBytes,
        downloadedMB: formatMB(downloadedBytes),
        totalMB: totalBytes ? formatMB(totalBytes) : null,
        isDownloading,
        isComplete,
        error,
        startDownload,
        openInstallModal,
        closeInstallModal,
        installModalPlatform,
      }}
    >
      {children}
      <WindowsInstallModal
        isOpen={installModalPlatform === "windows"}
        setIsOpen={(open) => {
          if (!open) closeInstallModal();
        }}
      />
      <MacInstallModal
        isOpen={installModalPlatform === "mac"}
        setIsOpen={(open) => {
          if (!open) closeInstallModal();
        }}
      />
    </DownloadContext.Provider>
  );
};
